import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import TasksTable from '../components/tasks/TasksTable';
import TaskAssignmentWizard from '../components/TaskAssignmentWizard';
import FilterFieldSelect from '../components/forms/FilterFieldSelect';
import FilterFieldCheck from '../components/forms/FilterFieldCheck';
import useAdminTasks from '../hooks/useAdminTasks';
import useTrackPageVisit from '../hooks/useTrackPageVisit';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle';

function tasksPageTitle(searchParams) {
  if (searchParams.get('created_by_me') === '1') return 'Tasks you created';
  const st = searchParams.get('status') || 'open';
  if (st === 'all') return 'Tasks';
  if (st === 'closed') return 'Closed tasks';
  return 'Open tasks';
}

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open only' },
  { value: 'all', label: 'All statuses' },
  { value: 'closed', label: 'Closed only' },
];

const KIND_OPTIONS = [
  { value: '', label: 'Maintenance only' },
  { value: 'feedback', label: 'Feedback only' },
  { value: 'all', label: 'All kinds' },
];

export default function TasksPage() {
  useTrackPageVisit('tasks');
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: tasks, refresh } = useAdminTasks();

  const title = useMemo(() => tasksPageTitle(searchParams), [searchParams]);
  useDocumentTitle(`${title} – Maintella`);

  const status = searchParams.get('status') || 'open';
  const kind = searchParams.get('kind') || '';
  const createdByMe = searchParams.get('created_by_me') === '1';

  const emptyMessage = useMemo(() => {
    if (createdByMe) return 'No tasks match “created by me” for these filters.';
    if (status === 'closed') return 'No closed tasks found.';
    if (status === 'all') return 'No tasks found.';
    return 'No open tasks found.';
  }, [createdByMe, status]);

  const [showWizard, setShowWizard] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  const setStatusFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'open') next.delete('status');
    else next.set('status', value);
    setSearchParams(next, { replace: true });
  };

  const setKindFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (!value) next.delete('kind');
    else next.set('kind', value);
    setSearchParams(next, { replace: true });
  };

  const setCreatedByMe = (checked) => {
    const next = new URLSearchParams(searchParams);
    if (checked) next.set('created_by_me', '1');
    else next.delete('created_by_me');
    setSearchParams(next, { replace: true });
  };

  const searchParamsKey = searchParams.toString();

  const taskFilterExtras = useMemo(
    () => (
      <>
        <FilterFieldSelect
          id="tasks-filter-status"
          label="Status"
          value={status}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={STATUS_OPTIONS}
        />
        <FilterFieldSelect
          id="tasks-filter-kind"
          label="Task kind"
          value={kind}
          onChange={(e) => setKindFilter(e.target.value)}
          options={KIND_OPTIONS}
        />
        <FilterFieldCheck
          id="tasks-filter-created-by-me"
          label="Created by me"
          checked={createdByMe}
          onChange={(e) => setCreatedByMe(e.target.checked)}
        />
      </>
    ),
    // searchParamsKey keeps URL-driven handlers in sync; value props track filter state
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParamsKey, status, kind, createdByMe]
  );

  const handleAssign = (task) => {
    setSelectedTaskId(task.id);
    setShowWizard(true);
  };

  const handleWizardClose = () => {
    setShowWizard(false);
    setSelectedTaskId(null);
    refresh();
  };

  return (
    <>
      <ObjectTopBar title={title} />
      <div className="container py-4">
        <TasksTable
          items={tasks}
          onAssign={handleAssign}
          emptyMessage={emptyMessage}
          filterExtras={taskFilterExtras}
        />
      </div>
      <TaskAssignmentWizard
        show={showWizard}
        onClose={handleWizardClose}
        initialTaskId={selectedTaskId}
      />
    </>
  );
}
